import { useIntl } from "react-intl";
import { Link } from "react-router";
import { useParams } from "react-router-dom";
import { StyledButton } from "~Common/Components";
import PermissionCheck from "~Common/PermissionCheck";
import { DataTableImageColumn } from "~components/DataTable/components/DataTableImageColumn/DataTableImageColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { CompetitionTableColumns } from "~pages/Competitions/CompetitionListPage/enums/competitionTableColumns.ts";
import type { CompetitionTransformed } from "~pages/Competitions/types/competitionTransformed.ts";
import type { League } from "~types/league.ts";
import { sortStringValues } from "~utils/utilities.ts";

export const useGetCompetitionsTableColumns = (): Array<DataTableColumn<CompetitionTransformed>> => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, customerId } = useParams();
  return [
    {
      title: formatMessage({ id: "competitionsListPage.table.column.competitionId" }),
      dataIndex: "competitionId",
      key: CompetitionTableColumns.CompetitionId,
      align: "center",
      copyable: true,
      width: 280,
    },
    {
      title: formatMessage({ id: "competitionsListPage.table.column.externalId" }),
      dataIndex: "externalId",
      key: CompetitionTableColumns.ExternalId,
      sorter: (a: CompetitionTransformed, b: CompetitionTransformed) => sortStringValues(a.externalId ?? "", b.externalId ?? ""),
      align: "center",
      copyable: true,
      width: 150,
    },
    {
      dataIndex: "images",
      key: CompetitionTableColumns.Images,
      title: formatMessage({ id: "competitionsListPage.table.column.image" }),
      align: "center",
      render: (images) => <DataTableImageColumn images={images} imageTypes={[ImageTypes.Logo]} />,
      width: 80,
    },
    {
      title: formatMessage({ id: "competitionsListPage.table.column.nameLocal" }),
      dataIndex: "nameLocal",
      sorter: (a: CompetitionTransformed, b: CompetitionTransformed) => sortStringValues(a.nameLocal ?? "", b.nameLocal ?? ""),
      key: CompetitionTableColumns.NameLocal,
    },
    {
      title: formatMessage({ id: "competitionsListPage.table.column.nameLatin" }),
      dataIndex: "nameLatin",
      sorter: (a: CompetitionTransformed, b: CompetitionTransformed) => sortStringValues(a.nameLatin ?? "", b.nameLatin ?? ""),
      key: CompetitionTableColumns.NameLatin,
    },
    {
      title: formatMessage({ id: "competitionsListPage.table.column.abbreviationLocal" }),
      dataIndex: "abbreviationLocal",
      key: CompetitionTableColumns.AbbreviationLocal,
      sorter: (a: CompetitionTransformed, b: CompetitionTransformed) =>
        sortStringValues(a.abbreviationLocal ?? "", b.abbreviationLocal ?? ""),
      width: 90,
      align: "center",
    },
    {
      title: formatMessage({ id: "competitionsListPage.table.column.abbreviationLatin" }),
      dataIndex: "abbreviationLatin",
      key: CompetitionTableColumns.AbbreviationLatin,
      sorter: (a: CompetitionTransformed, b: CompetitionTransformed) =>
        sortStringValues(a.abbreviationLatin ?? "", b.abbreviationLatin ?? ""),
      width: 90,
      align: "center",
    },
    {
      title: formatMessage({ id: "competitionsListPage.table.column.internationalReference" }),
      dataIndex: "internationalReference",
      key: CompetitionTableColumns.InternationalReference,
      sorter: (a: CompetitionTransformed, b: CompetitionTransformed) =>
        sortStringValues(a.internationalReference ?? "", b.internationalReference ?? ""),
      width: 100,
    },
    {
      title: formatMessage({ id: "competitionsListPage.table.column.ageGroup" }),
      dataIndex: "ageGroup",
      key: CompetitionTableColumns.AgeGroup,
      sorter: (a: CompetitionTransformed, b: CompetitionTransformed) => sortStringValues(a.ageGroup ?? "", b.ageGroup ?? ""),
      width: 100,
      align: "center",
    },
    {
      title: formatMessage({ id: "competitionsListPage.table.column.league" }),
      dataIndex: "league",
      key: CompetitionTableColumns.League,
      sorter: (a: CompetitionTransformed, b: CompetitionTransformed) =>
        sortStringValues(a.league?.nameLocal ?? "", b.league?.nameLocal ?? ""),
      width: 150,
      align: "center",
      render: (value: League | null) => (value ? value.nameLocal : ""),
    },
    {
      align: "right",
      persistent: true,
      fixed: "right",
      width: 50,
      disableRowNavigation: true,
      render: (_: any, row) => (
        <PermissionCheck permissionKeys={[PermissionNames.SEASON]}>
          <Link to={`/${customerId}/admin/${sport}/${organizationId}/competition/${row.competitionId}/seasons/`}>
            <StyledButton type={"text"}>{formatMessage({ id: "seasons" })}</StyledButton>
          </Link>
        </PermissionCheck>
      ),
    },
  ];
};
